import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';

const ForEnterprisesPage: React.FC = () => {
    return (
        <div className="bg-background min-h-screen">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-b from-muted/50 to-background pt-24 pb-16">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <span className="inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium bg-muted/50 text-muted-foreground mb-6 backdrop-blur-sm">
                            For Enterprises
                        </span>
                        <h1 className="text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
                            Build Internal Tools with Python, Deploy with Confidence
                        </h1>
                        <p className="text-xl text-muted-foreground mb-8">
                            Transform your Python scripts into secure, interactive web applications. No frontend development or infrastructure management required.
                        </p>
                        <div className="flex justify-center gap-4 text-lg font-semibold">
                            <span className="text-primary">Python-native</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-primary">Zero DevOps</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-primary">Enterprise-ready</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Enterprise Use Cases */}
            <section className="py-16 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Real-World Enterprise Applications</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Data Science Workflows</h3>
                                <p className="text-muted-foreground">Convert Jupyter notebooks into interactive dashboards. Share insights with stakeholders through secure, self-contained applications.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Internal Tools</h3>
                                <p className="text-muted-foreground">Build admin panels, data entry forms, and approval workflows. Deploy directly to your internal network without backend setup.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Business Intelligence</h3>
                                <p className="text-muted-foreground">Create interactive reports and analytics dashboards. Connect to your data sources and deploy to your internal cloud.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Process Automation</h3>
                                <p className="text-muted-foreground">Build tools for data processing, validation, and transformation. Package complex workflows into user-friendly applications.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Benefits */}
            <section className="py-16 bg-muted/50 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Why Enterprises Choose Preswald</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Python-First Development</h3>
                                <p className="text-muted-foreground">Write your business logic in Python. Preswald handles the UI, state management, and deployment. No JavaScript required.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Zero Infrastructure</h3>
                                <p className="text-muted-foreground">Generate static web applications that run entirely in the browser. Deploy to any static hosting or internal server.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Enterprise Security</h3>
                                <p className="text-muted-foreground">All data processing happens client-side. No data leaves your network. Compatible with strict security policies.</p>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">Rapid Development</h3>
                                <p className="text-muted-foreground">Go from Python script to production-ready application in hours. Perfect for quick prototypes and time-sensitive projects.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Strategic Benefits */}
            <section className="py-16 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h2 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors">Business Impact</h2>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Reduce development time by 80% compared to traditional web apps</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Enable data scientists to build tools without frontend expertise</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Deploy applications without DevOps or infrastructure changes</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="group rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50">
                            <div className="p-6">
                                <h2 className="text-2xl font-bold mb-6 group-hover:text-primary transition-colors">Enterprise Requirements</h2>
                                <ul className="space-y-4">
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Compliant with data privacy and security regulations</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>Works in air-gapped and restricted environments</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-primary mr-2">✓</span>
                                        <span>No external dependencies or runtime requirements</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 bg-muted/50 relative">
                <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
                <div className="container relative mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-8">Ready to Transform Your Python Workflows?</h2>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <a 
                            href="/"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8"
                        >
                            See the Enterprise Templates
                        </a>
                        <button 
                            onClick={() => window.open('https://cal.com/amruthagujjar/30min', '_blank')}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8"
                        >
                            Book a Demo
                        </button>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default ForEnterprisesPage; 